import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, Scissors, Clock } from "lucide-react";
import { BUSINESS } from "../data/site";
import { useCoupons, FALLBACK_COUPON, discountText } from "../hooks/useCoupons";

export const CouponPopup = () => {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const coupons = useCoupons();
  const coupon = coupons && coupons.length > 0 ? coupons[0] : FALLBACK_COUPON;

  useEffect(() => {
    if (isAdmin) return;
    if (sessionStorage.getItem("baysmog_coupon_seen")) return;
    let t;
    const arm = () => {
      t = setTimeout(() => {
        sessionStorage.setItem("baysmog_coupon_seen", "1");
        setShow(true);
      }, 1800);
    };
    if (document.readyState === "complete") arm();
    else {
      window.addEventListener("load", arm, { once: true });
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener("load", arm);
    };
  }, [isAdmin]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setShow(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <AnimatePresence>
      {show && !isAdmin && (
        <motion.div
          data-testid="coupon-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/50 backdrop-blur-sm"
          onClick={() => setShow(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Smog check coupon offer"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border-2 border-dashed border-volt p-8 sm:p-10 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="coupon-popup-close"
              onClick={() => setShow(false)}
              aria-label="Close coupon popup"
              className="absolute top-3.5 right-3.5 w-9 h-9 rounded-full bg-paper flex items-center justify-center text-slate-500 hover:text-ink hover:bg-line transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
            <Scissors className="absolute -top-4 -left-4 w-9 h-9 text-volt bg-white rounded-full p-1.5 shadow-md" aria-hidden="true" />

            <p className="inline-flex items-center gap-2 bg-volt-dim text-volt font-bold text-[11px] uppercase tracking-[0.2em] px-4 py-1.5 rounded-full">
              <Clock className="w-3.5 h-3.5" /> Limited Walk-In Offer
            </p>
            <p className="mt-5 font-display font-black uppercase leading-none text-6xl text-volt">
              {discountText(coupon)}
            </p>
            <p className="mt-2 font-display font-black uppercase tracking-tight text-2xl text-ink">
              {coupon.title}
            </p>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">
              {coupon.description || FALLBACK_COUPON.description}
            </p>

            <a
              data-testid="coupon-popup-call"
              href={BUSINESS.phoneHref}
              className="mt-7 flex items-center justify-center gap-2.5 bg-gradient-to-r from-cta to-[#F59E0B] text-ink rounded-full font-display font-bold uppercase tracking-wide text-sm px-8 py-4 shadow-lg shadow-cta/40 hover:brightness-95 hover:-translate-y-0.5 transition-[transform,filter] duration-300"
            >
              <Phone className="w-4 h-4" />
              Call to Redeem — {BUSINESS.phone}
            </a>
            <button
              data-testid="coupon-popup-dismiss"
              onClick={() => setShow(false)}
              className="mt-4 text-xs font-semibold text-slate-400 hover:text-ink underline underline-offset-4 transition-colors duration-200"
            >
              No thanks, maybe later
            </button>
            <p className="mt-4 font-mono text-[10px] text-slate-400">
              {coupon.terms || "Certificate fee may apply · Not valid with other offers"}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
